// 機關時間軸的高度：上緣把手上下拖、雙擊回到預設；每個看的人各自記住（這台電腦的瀏覽器），導演頁 / 編輯頁 / Cue 庫頁分開記。
// 不能蓋掉 3D 畫面：最高約視窗的 60%，而且 3D 畫面至少留約 200px；視窗變矮時自動收回範圍內。
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

export const TL_HEIGHT_DEFAULT = 240;
export const TL_HEIGHT_MIN = 140;
export const TL_HEIGHT_MAX_RATIO = 0.6;
export const TL_SCENE_MIN = 200;

const storageKey = (key: string) => `stage-previz:mech-timeline-height:${key}`;

/** h 收進範圍內；room = 量到的「時間軸最多能佔多高」（量不到不給）。視窗太矮時下限優先（CSS 另外保證 3D 不會被擠到 0） */
export function clampHeight(h: number, viewportH: number, room?: number): number {
  const v = Number.isFinite(h) ? h : TL_HEIGHT_DEFAULT;
  const hi = Math.max(TL_HEIGHT_MIN, Math.min(viewportH * TL_HEIGHT_MAX_RATIO, viewportH - TL_SCENE_MIN, room ?? Infinity));
  return Math.round(Math.max(TL_HEIGHT_MIN, Math.min(hi, v)));
}

export function readHeight(key: string): number {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : TL_HEIGHT_DEFAULT;
  } catch {
    return TL_HEIGHT_DEFAULT;
  }
}

/** null = 清掉（回到預設） */
export function writeHeight(key: string, h: number | null): void {
  try {
    if (h === null) window.localStorage.removeItem(storageKey(key));
    else window.localStorage.setItem(storageKey(key), String(h));
  } catch { /* 存不了就只在這次有效 */ }
}

const viewportNow = () => (typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 900);

/** 上層扣掉別的固定高度的區塊（會伸縮的 3D 畫面不算），再留 3D 的最小高度 → 時間軸最多能佔多高 */
function measureRoom(section: HTMLElement | null): number | undefined {
  const parent = section?.parentElement;
  if (!section || !parent || parent.clientHeight <= 0) return undefined;
  let others = 0;
  for (const c of Array.from(parent.children)) {
    if (c === section) continue;
    const cs = window.getComputedStyle(c);
    if (cs.position === 'absolute' || cs.position === 'fixed' || parseFloat(cs.flexGrow) > 0) continue;
    others += c.getBoundingClientRect().height;
  }
  return parent.clientHeight - others - TL_SCENE_MIN;
}

export function useTimelineHeight(key: string, sectionRef: RefObject<HTMLElement>) {
  const [pref, setPref] = useState(() => readHeight(key));
  const [viewport, setViewport] = useState(viewportNow);
  const [room, setRoom] = useState<number | undefined>(undefined);
  const height = clampHeight(pref, viewport, room);
  const dragRef = useRef<{ y0: number; h0: number; h: number } | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;

  useEffect(() => { setPref(readHeight(key)); }, [key]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const measure = () => { setViewport(viewportNow()); setRoom(measureRoom(sectionRef.current)); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [sectionRef]);

  const handleProps = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button > 0) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const r = measureRoom(sectionRef.current);
      setRoom(r);
      roomRef.current = r;
      dragRef.current = { y0: e.clientY, h0: height, h: height };
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const d = dragRef.current;
      if (!d) return;
      d.h = clampHeight(d.h0 + d.y0 - e.clientY, viewportNow(), roomRef.current);
      setPref(d.h);
    },
    onPointerUp: () => {
      const d = dragRef.current;
      dragRef.current = null;
      if (d) writeHeight(key, d.h);
    },
    onPointerCancel: () => {
      const d = dragRef.current;
      dragRef.current = null;
      if (d) setPref(d.h0);
    },
    onDoubleClick: () => {
      dragRef.current = null;
      setPref(TL_HEIGHT_DEFAULT);
      writeHeight(key, null);
    },
  };
  return { height, handleProps };
}
